import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

let csrfToken: string | null = null;

async function fetchCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  const { data } = await axios.get(`${API_URL}/csrf-token`, { withCredentials: true });
  csrfToken = data.csrfToken;
  return csrfToken!;
}

const apiClient = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

apiClient.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Attach CSRF token for state-changing methods
  const method = config.method?.toUpperCase();
  if (method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrf = await fetchCsrfToken();
    config.headers['x-csrf-token'] = csrf;
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    // If CSRF token expired/invalid, refresh it and retry once
    if (error.response?.status === 403 && error.response?.data?.error?.code === 'CSRF_ERROR' && !original._csrfRetry) {
      original._csrfRetry = true;
      csrfToken = null;
      const csrf = await fetchCsrfToken();
      original.headers['x-csrf-token'] = csrf;
      return apiClient(original);
    }

    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
          localStorage.setItem('accessToken', data.accessToken);
          localStorage.setItem('refreshToken', data.refreshToken);
          original.headers.Authorization = `Bearer ${data.accessToken}`;
          return apiClient(original);
        } catch {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/admin/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
